import { apiFetch, isErrorResult } from "../../../../app/apiClient.js";
import { getCachedTrackedWorkMs } from "../../app-dialog/utils/dropdowns/dropdownManager.js";
import { allActivities } from "../../../../app/utils/enums.js";
import { validateData, checkTrackedHours, getContent, removeStaleRow, deleteRecord } from "./tableManager.js";
import { timeToHours } from "../utils/timeUtils.js";
import { updateFilterOptions } from "../utils/filterManager.js";
import { toast } from "../../../layout/app-toast/utils/toastManager.js";
import { currentTable, getDateFilter } from "../../../../app/viewManager.js";
import { getOpenTaskId, ensureOpenTaskId, setOpenTaskId } from "../../app-time/utils/timeManager.js";
import { formatDateISO } from "../../../shared/app-input/utils/dateUtils.js";

function formatEntries(data) {
    return data.map(entry => {
        entry.activityName = allActivities[entry.activityId] ?? 'Unknown';
        entry.isRunning = entry.workedHours == null;
        return entry;
    });
}

function compareEntries(a, b) {
    return new Date(b.date) - new Date(a.date) || new Date(b.createdAt) - new Date(a.createdAt);
}

async function collectEntryData() {
    const fieldMapping = {
        CustomerId: "#dropdownCustomer",
        ApplicationId: "#dropdownApplication",
        ProjectId: "#dropdownProject",
        ActivityId: "#dropdownActivity",
        Description: "#inputDescription",
        PayerId: "#dropdownBillingVia",
        Date: "#inputDate",
        WorkedHours: "#inputHoursWorked"
    };

    try {
        const content = getContent();
        if (!content) throw new Error("Dialog content nicht gefunden");

        const data = {};
        for (const [key, selector] of Object.entries(fieldMapping)) {
            const element = content.querySelector(selector);
            if (!element) throw new Error(`Element ${selector} nicht gefunden`);

            let value = element.tagName === 'APP-INPUT' ? (element.getValue() ?? '') : element.value;
            if (key === "WorkedHours") {
                value = (value ? timeToHours(value) : 0) || null;
            }
            if (key === "PayerId") value = value || null;
            data[key] = value;
        }

        data.UserId = sessionStorage.getItem("currentUser") || null;
        return data;
    } catch (error) {
        console.error("Fehler beim Ermitteln der Daten:", error.message);
        return null;
    }
}

export async function loadEntries(table) {
    await ensureOpenTaskId();
    const entryTable = table ?? currentTable;
    if (!entryTable) return false;

    const params = new URLSearchParams();

    const dateFilter = getDateFilter('Entries');
    let clientMonthFilter = null;

    if (dateFilter?.mode === 'exact') {
        if (dateFilter.from) params.append('from', dateFilter.from);
        if (dateFilter.to)   params.append('to',   dateFilter.to);
    } else if (dateFilter?.mode === 'coarse') {
        const year   = dateFilter.year;
        const months = Array.isArray(dateFilter.months) ? dateFilter.months : [];
        if (year && months.length === 1) {
            const monthNum = parseInt(months[0], 10);
            params.append('from', formatDateISO(new Date(Number(year), monthNum - 1, 1)));
            params.append('to',   formatDateISO(new Date(Number(year), monthNum,     0)));
        } else if (year && months.length > 1) {
            params.append('from', `${year}-01-01`);
            params.append('to',   `${year}-12-31`);
            clientMonthFilter = months.map(month => parseInt(month, 10));
        } else if (year) {
            params.append('from', `${year}-01-01`);
            params.append('to',   `${year}-12-31`);
        }
    }

    const filterValue = sessionStorage.getItem('filterEntries') || 'user';
    if (filterValue === 'user') {
        params.append('scope', 'User');
    } else if (filterValue === 'team') {
        params.append('scope', 'Team');
    } else if (filterValue === 'all') {
        params.append('scope', 'All');
    }

    const query = params.toString();

    let data = await apiFetch(query ? `Entries?${query}` : "Entries");

    entryTable.options.placeholder = "Keine Daten vorhanden";

    if (!Array.isArray(data)) {
        entryTable.replaceData([]);
        return false;
    }

    data.sort(compareEntries);

    if (clientMonthFilter) {
        data = data.filter(entry => {
            const entryDate = entry.date ? new Date(entry.date) : null;
            return entryDate && clientMonthFilter.includes(entryDate.getMonth() + 1);
        });
    }

    const entries = formatEntries(data);
    entryTable.setData(entries);
    updateFilterOptions(entries, entryTable);
    return true;
}

export async function createEntry(isTask = false) {
    const collectedData = await collectEntryData();
    if (!collectedData) return;

    collectedData.StartWorkday = !!isTask;

    const missingFields = await validateData(collectedData);
    if (missingFields.length > 0) return true;

    const raw = await apiFetch("Entries", "POST", collectedData);
    if (isErrorResult(raw)) return raw ? true : undefined;
    const newEntry = formatEntries([raw])[0];

    const trackedExceeded = !raw.warning && await checkTrackedHours(collectedData, getCachedTrackedWorkMs());

    if (currentTable?.tableName === "entry") {
        const sorters = currentTable.getSorters();
        if (sorters.length) {
            await currentTable.addRow(newEntry, true);
            currentTable.setSort(sorters.map(s => ({ column: s.field, dir: s.dir })));
        } else {
            const insertBeforeRow = currentTable.getData().find(row => compareEntries(newEntry, row) < 0);
            if (insertBeforeRow) {
                await currentTable.addRow(newEntry, true, insertBeforeRow.id);
            } else {
                await currentTable.addRow(newEntry, false);
            }
        }
        updateFilterOptions(currentTable.getData(), currentTable);
    }

    if (collectedData.WorkedHours === null) {
        setOpenTaskId(raw.id);
    }

    if (raw.warning) {
        toast(raw.warning, "warning");
    } else if (!trackedExceeded) {
        toast("Eintrag erfolgreich hinzugefügt!", "success");
    }

    return raw;
}

export async function updateEntry(entryId) {
    const collectedData = await collectEntryData();
    if (!collectedData) return;

    collectedData.id = entryId;

    const missingFields = await validateData(collectedData);
    if (missingFields.length > 0) return true;

    const raw = await apiFetch(`Entries/${entryId}`, "PATCH", collectedData);
    if (removeStaleRow(raw, entryId)) return;
    if (isErrorResult(raw)) return raw ? true : undefined;

    const trackedExceeded = !raw.warning && await checkTrackedHours(collectedData, getCachedTrackedWorkMs());

    if (collectedData.WorkedHours === null) {
        setOpenTaskId(entryId);
    } else if (getOpenTaskId() === entryId) {
        setOpenTaskId(null);
    }

    const updatedEntry = formatEntries([raw])[0];
    currentTable.updateData([updatedEntry]);
    updateFilterOptions(currentTable.getData(), currentTable);

    if (raw.warning) {
        toast(raw.warning, "warning");
    } else if (!trackedExceeded) {
        toast("Eintrag erfolgreich aktualisiert!", "success");
    }
    return raw;
}

export const deleteEntry = (entryId) => deleteRecord("Entries", entryId, "Eintrag erfolgreich gelöscht!", undefined, { idsInBody: true });

async function changeEntryStatus(entryId, status) {
    const ids = Array.isArray(entryId) ? entryId : [entryId];
    if (!ids.length) return;
    const body = ids.map(id => ({ entryId: id, isFactored: status }));

    const result = await apiFetch("Entries/changeStatus", "PATCH", body);
    if (isErrorResult(result)) return;

    let successIds = ids;
    if (Array.isArray(result)) {
        const failedIds = new Set(result.filter(item => item.error).map(item => item.entryId));
        result.forEach(item => { if (item.error) toast(item.error, "error"); });
        successIds = ids.filter(id => !failedIds.has(id));
    }

    if (currentTable && successIds.length) {
        currentTable.updateData(successIds.map(id => ({ id, isFactored: status })));
        updateFilterOptions(currentTable.getData(), currentTable);
    }

    if (successIds.length) {
        const noun = successIds.length === 1 ? "Eintrag" : "Einträge";
        const verb = status ? "fakturiert" : "als nicht fakturiert markiert";
        toast(`${successIds.length} ${noun} ${verb}`, "success");
    }
}

export const changeFactoring = (entryId, status) => changeEntryStatus(entryId, status);
export const markFactored = (entryId) => changeEntryStatus(entryId, true);